import { useState, useEffect } from 'react';
import { getAuthToken, getUserData, validateToken, logout } from '../utils/auth';

interface AuthState {
  isAuthenticated: boolean;
  user: { id: string; email: string; name: string } | null;
  isLoading: boolean;
}

export const useAuth = () => {
  const [authState, setAuthState] = useState<AuthState>({
    isAuthenticated: false,
    user: null,
    isLoading: true,
  });

  // Validate token on mount
  useEffect(() => {
    const initAuth = async () => {
      const token = getAuthToken();
      const userData = getUserData();

      if (token && userData) {
        const isValid = await validateToken();

        if (isValid) {
          setAuthState({
            isAuthenticated: true,
            user: userData,
            isLoading: false,
          });
        } else {
          setAuthState({
            isAuthenticated: false,
            user: null,
            isLoading: false,
          });
        }
      } else {
        setAuthState({
          isAuthenticated: false,
          user: null,
          isLoading: false,
        });
      }
    };

    initAuth();
  }, []);

  // Periodically validate token (every 5 minutes)
  useEffect(() => {
    if (!authState.isAuthenticated) return;

    const interval = setInterval(async () => {
      const isValid = await validateToken();

      if (!isValid) {
        setAuthState({
          isAuthenticated: false,
          user: null,
          isLoading: false,
        });
      }
    }, 5 * 60 * 1000); // 5 minutes

    return () => clearInterval(interval);
  }, [authState.isAuthenticated]);

  const handleLogout = () => {
    logout();
    setAuthState({
      isAuthenticated: false,
      user: null,
      isLoading: false,
    });
  };

  const setAuth = (user: { id: string; email: string; name: string }) => {
    setAuthState({
      isAuthenticated: true,
      user,
      isLoading: false,
    });
  };

  return {
    ...authState,
    logout: handleLogout,
    setAuth,
  };
};
